import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { DashboardStatsGrid, DashboardQuickActions, DashboardNotifications, DashboardCTA } from '../components/dashboard/DashboardWidgets';
import { ShieldCheck, Users, Briefcase, Award, Headphones, Truck, Building2, Globe, FileText, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../utils/logger';

const ROLE_LABELS = { driver: 'Driver', carrier_owner: 'Carrier Owner', dispatcher: 'Dispatcher' };

const QUICK_ACTIONS_BY_ROLE = {
  driver: [
    { icon: ShieldCheck, label: 'Upload Credentials', link: '/profile', color: 'bg-blue-50 text-blue-600' },
    { icon: Briefcase, label: 'Browse Jobs', link: '/jobs', color: 'bg-green-50 text-green-600' },
    { icon: Users, label: 'Refer a Friend', link: '/referrals', color: 'bg-purple-50 text-purple-600' },
    { icon: Award, label: 'Resources', link: '/resources', color: 'bg-orange-50 text-orange-600' }
  ],
  carrier_owner: [
    { icon: Building2, label: 'Business Profile', link: '/profile', color: 'bg-green-50 text-green-600' },
    { icon: Globe, label: 'Load Boards', link: '/external-job-boards', color: 'bg-blue-50 text-blue-600' },
    { icon: Users, label: 'Hire Drivers', link: '/jobs', color: 'bg-purple-50 text-purple-600' },
    { icon: FileText, label: 'Resources', link: '/resources', color: 'bg-orange-50 text-orange-600' }
  ],
  dispatcher: [
    { icon: Headphones, label: 'Dispatcher Profile', link: '/profile', color: 'bg-purple-50 text-purple-600' },
    { icon: Truck, label: 'Find Carriers', link: '/jobs', color: 'bg-green-50 text-green-600' },
    { icon: Users, label: 'Grow Network', link: '/referrals', color: 'bg-blue-50 text-blue-600' },
    { icon: Award, label: 'Resources', link: '/resources', color: 'bg-orange-50 text-orange-600' }
  ]
};

const Dashboard = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentChecking, setPaymentChecking] = useState(false);

  useEffect(() => { if (user?.is_admin) navigate('/admin', { replace: true }); }, [user, navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const [profileRes, referralRes, jobRes] = await Promise.allSettled([
        api.get('/profile'), api.get('/referrals/stats'), api.get('/jobs/all')
      ]);
      const profile = profileRes.status === 'fulfilled' ? profileRes.value.data : {};
      const referrals = referralRes.status === 'fulfilled' ? referralRes.value.data : {};
      const jobs = jobRes.status === 'fulfilled' ? jobRes.value.data : {};
      const badges = profile?.badges || [];
      const verifiedBadges = badges.filter(b => b.status === 'verified').length;
      const totalBadges = badges.length || 1;
      setStats({
        verification_percentage: Math.round((verifiedBadges / totalBadges) * 100),
        verified_badges: verifiedBadges, total_badges: badges.length,
        total_referrals: referrals?.total_referrals || 0,
        earnings: referrals?.total_earned || 0, active_jobs: jobs?.total || 0
      });
    } catch (error) {
      logger.error('Stats fetch error:', error);
      setStats({ verification_percentage: 0, verified_badges: 0, total_badges: 0, total_referrals: 0, earnings: 0, active_jobs: 0 });
    } finally { setLoading(false); }
  }, []);

  const checkPaymentStatus = useCallback(async () => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;
    setPaymentChecking(true);
    let attempts = 0;
    const poll = async () => {
      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        if (response.data.payment_status === 'paid') { toast.success('Payment successful! Your membership has been upgraded.'); fetchStats(); return; }
        if (response.data.status === 'expired') { toast.error('Payment session expired. Please try again.'); return; }
        if (++attempts < 5) setTimeout(poll, 2000); else toast.info('Payment is being processed. Check back shortly.');
      } catch (error) { logger.error('Payment status check error:', error); }
      finally { setPaymentChecking(false); }
    };
    poll();
  }, [searchParams, fetchStats]);

  useEffect(() => { fetchStats(); checkPaymentStatus(); }, [fetchStats, checkPaymentStatus]);

  const userRole = user?.role || 'driver';
  const quickActions = QUICK_ACTIONS_BY_ROLE[userRole] || QUICK_ACTIONS_BY_ROLE.driver;

  const notifications = [
    { type: 'success', message: 'Welcome to StraightConnect! Complete your profile to get started.' },
    ...(userRole === 'driver' ? [{ type: 'info', message: 'New jobs matching your profile are available.' }] :
        userRole === 'carrier_owner' ? [{ type: 'info', message: 'Check out new load board opportunities.' }] :
        [{ type: 'info', message: 'Carriers are looking for dispatch services.' }])
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent" />
          <p className="mt-4 text-slate-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900" data-testid="dashboard-title">Welcome back, {user?.name}</h1>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${userRole === 'driver' ? 'bg-blue-100 text-blue-800' : userRole === 'carrier_owner' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'}`} data-testid="user-role-badge">
              {ROLE_LABELS[userRole] || 'Member'}
            </span>
          </div>
          <p className="text-slate-600">Here's what's happening with your logistics network today</p>
        </motion.div>

        {paymentChecking && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-md mb-6 flex items-center gap-2">
            <div className="h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /><span>Verifying payment status...</span>
          </div>
        )}

        <DashboardStatsGrid stats={stats} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <DashboardQuickActions quickActions={quickActions} />
          <DashboardNotifications notifications={notifications} />
        </div>

        <DashboardCTA />

        {user?.role === 'dispatcher' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
            className="bg-white border border-slate-200 rounded-md p-6 shadow-sm mt-6">
            <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Featured Dispatch Partner</h2>
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <div className="flex-1">
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">S Connect Sync Route Dispatcher</h3>
                <p className="text-slate-600 text-sm mb-3">Helping small carriers maximize revenue and minimize downtime with 24/7 support, load sourcing, and back-office management.</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">FMCSA Compliant</span>
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Verified Partner</span>
                  <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">Since 2021</span>
                </div>
              </div>
              <Link to="/dispatcher" className="bg-slate-900 text-white px-6 py-3 rounded-md font-semibold hover:bg-slate-800 transition-all btn-hover-lift whitespace-nowrap" data-testid="view-dispatcher-btn">
                View Profile <ArrowRight className="inline ml-2 h-5 w-5" />
              </Link>
            </div>
          </motion.div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Dashboard;
